<template>
  <div class="dropdown-main">
    <Input :inputValue.sync="dropdownText" class="dropdown-input" ref="input" :placeholder="placeholder"
      :textAlign="textAlign" :isDisabled="isDisabled" :isRequired="isRequired" :errorMessage.sync="privateErrorMessage"
      :validator="validator" :validatorFunction="validatorFunction"
      @focusChanged="inputFocusChanged" @inputChanged="dropdownTextInputChanged" @keydown.native="inputKeyDown"
    >
      <template slot="prefix"><slot name="prefix"></slot></template>
      <template slot="label-suffix"><slot name="label-suffix"></slot></template>
    </Input>

    <div class="arrow-container" @mousedown="arrowClicked">
      <div :class="[ 'arrow-down', { 'arrow-flip': inputFocused, 'arrow-disabled': isDisabled } ]"></div>
    </div>

    <div v-if="inputFocused" :class="['dropdown-list']" @scroll.passive="dropdownListScrolled" ref="dropdownList">
      <div class="filtered-caption" >
        <slot name="list-caption"></slot>
        <div class="filtered-count">
          <div v-if="isLoading">Loading...</div>
          <div v-else>{{ filteredItemsCount }} of {{ totalItems }} items</div>
          <img v-if="!isLoading" title="Clear search and selected item" src="https://dsmapi.logistics.systems/dsm6gIcons/Clear%20Filter.png" height="15px"
            @mousedown="clearFilterClicked" />
        </div>
      </div>

      <div :style="dropdownItemsContainerStyling">
        <div v-if="isLoading" class="dropdown-items item-loading" :style="dropdownItemsStyling">Loading...</div>
        <template v-else>
          <div v-if="noMatchFoundIndicatorIsVisible" class="dropdown-items item-error" :style="dropdownItemsStyling" >{{noMatchFoundIndicatorText}}</div>
          <div v-else-if="useFreeTextIndicatorIsVisible" :class="['dropdown-items', { 'item-active': activeDropdownItemIndex === 0 }]" :style="dropdownItemsStyling"
            :title="useFreeTextIndicatorText" @mouseenter="dropdownItemMouseEnter(0)" @mouseleave="dropdownItemMouseLeave(0)"
          >
            {{useFreeTextIndicatorText}}
          </div>
          <div v-for="item in visibleMatchingItems" :key="item[dropdownIndexSymbol]" :class="['dropdown-items', { 'item-active': item[dropdownIndexSymbol] === activeDropdownItemIndex, 'item-checked': isItemChecked && isItemChecked(item) }]" :style="dropdownItemsStyling"
            :title="item[itemKey]" @mouseenter="dropdownItemMouseEnter(item[dropdownIndexSymbol])" @mouseleave="dropdownItemMouseLeave(item[dropdownIndexSymbol])"
            @mousedown="keepOpenOnSelect && dropdownItemMouseDown($event, item[dropdownIndexSymbol])"
          >
            <input v-if="isItemChecked" type="checkbox" class="dropdown-item-check" tabindex="-1" aria-hidden="true" :checked="isItemChecked(item)" />
            {{item[dropdownIndexSymbol]}}. {{item[itemKey]}}
          </div>
          <div v-if="splitterIsVisible" class="dropdown-splitter" :style="dropdownItemsStyling"></div>
          <div v-if="noItemsIndicatorIsVisible" class="dropdown-items item-error" :style="dropdownItemsStyling">No items</div>
          <div v-for="item in visibleDropdownItems" :key="item[dropdownIndexSymbol]" :class="['dropdown-items', { 'item-active': item[dropdownIndexSymbol] === activeDropdownItemIndex, 'item-checked': isItemChecked && isItemChecked(item) }]" :style="dropdownItemsStyling"
            :title="item[itemKey]" @mouseenter="dropdownItemMouseEnter(item[dropdownIndexSymbol])" @mouseleave="dropdownItemMouseLeave(item[dropdownIndexSymbol])"
            @mousedown="keepOpenOnSelect && dropdownItemMouseDown($event, item[dropdownIndexSymbol])"
          >
            <input v-if="isItemChecked" type="checkbox" class="dropdown-item-check" tabindex="-1" aria-hidden="true" :checked="isItemChecked(item)" />
            {{item[dropdownIndexSymbol]}}. {{item[itemKey]}}
          </div>
        </template>
      </div>
      <div v-show="false" ref="searchSection"></div>
    </div>
  </div>
</template>

<script>
const dropdownItemHeight = 23; // text 18px + top padding 2px + bottom padding 2px + spacing between each dropdown item 1px
const splitterHeight = 12; // actual splitter line height 2px + top margin 5px + bottom margin 5px

const numberOfPaddingDropdownItems = 30; // render 30 extra items as padding to ensure seamless scrolling
const dropdownItemPaddingHeight = dropdownItemHeight * numberOfPaddingDropdownItems; // added above and below the window

export default {
  components: {
  },
  computed: {
    // uses virtual scrolling to improve performance
    // the idea is to only render the items that is visible to the user, and update what is rendered when scroll position changes
    // can think of it as a sliding window that moves with the scroll position

    // encountered a performance issue with computed properties
    // it seems accessing a computed property is slow, even though the evaluated value is already cached
    // to fix this, store values from computed properties in a constant, and use that constant instead of accessing the same computed property multiple times
    // https://github.com/vuejs/vue/issues/6660

    totalItems: function() {
      return this.dropdownItems.length + this.matchingItems.length;
    },

    dropdownItemsContainerHeight: function() {
      let height = this.isLoading ? dropdownItemHeight : dropdownItemHeight * this.totalItems; // all dropdown items will always be shown on the dropdown list, just in different segments depending on the dropdown text entered

      if (!this.isLoading && this.showNoMatchFoundIndicator) {
        height += dropdownItemHeight; // no matches found indicator is the same height as the regular dropdown items
      } else if (!this.isLoading && this.showUseFreeTextIndicator) {
        height += dropdownItemHeight; // use free text indicator is the same height as the regular dropdown items
      }

      if (!this.isLoading && this.showSplitter) {
        height += splitterHeight;
      }

      if (!this.isLoading && this.showNoItemsIndicator) {
        height += dropdownItemHeight; // no items indicator is the same height as the regular dropdown items
      }

      return height;
    },
    dropdownItemsContainerStyling: function() {
      return {
        height: `${this.dropdownItemsContainerHeight}px`,
      };
    },

    dropdownItemsWindowPosition: function() {
      // the window is a subspace near the current scroll position, and items that fall within the window will be rendered
      // items rendered will appear at the top of the dropdown container, and each item is moved downwards with translate Y if necessary so that it apepars at the scroll position
      // the window contains some padding space at the top and bottom in order to render some extra items to ensure seamless scrolling
      // but the padding is only added when the scroll postion is not near the top or bottom
      // the window expands as the visible portion begins scrolling away from the top, and shrinks as the visible portion begin approaching the bottom, due to the padding changing

      const dropdownWindow = {};

      const fullyExpandedStartPosition = this.dropdownListScrollTop - dropdownItemPaddingHeight;
      if (fullyExpandedStartPosition > 0) { // when the scroll position is near the top, the top padding should be shrunk, and it will slowly expand as the user scrolls down, till the maximum height
        dropdownWindow.start = fullyExpandedStartPosition;
        dropdownWindow.topPaddingHeight = dropdownItemPaddingHeight;
      } else {
        dropdownWindow.start = 0;
        dropdownWindow.topPaddingHeight = this.dropdownListScrollTop;
      }

      const fullyExpandedEndPosition = this.dropdownListScrollTop + this.dropdownItemsWindowVisibleHeight + dropdownItemPaddingHeight;
      const containerHeight = this.dropdownItemsContainerHeight;
      if (fullyExpandedEndPosition < containerHeight) { // when the scroll position is near the bottom, the bottom padding should be shrunk, and it will slowly expand as the user scrolls up, till the maximum height
        dropdownWindow.end = fullyExpandedEndPosition;
        dropdownWindow.bottomPaddingHeight = dropdownItemPaddingHeight;
      } else {
        dropdownWindow.end = containerHeight;
        dropdownWindow.bottomPaddingHeight = dropdownWindow.end - dropdownWindow.start - dropdownWindow.topPaddingHeight - this.dropdownItemsWindowVisibleHeight;
      }

      return dropdownWindow;
    },
    dropdownItemsStyling: function() {
      const dropdownItemsWindowPosition = this.dropdownItemsWindowPosition;
      const dropdownItemsContainerHeight = this.dropdownItemsContainerHeight;

      let offset = dropdownItemsWindowPosition.start;
      if (dropdownItemsWindowPosition.start !== 0 && dropdownItemsWindowPosition.end === dropdownItemsContainerHeight) { // once the dropdown is scrolled all the way to the bottom
        offset += dropdownItemHeight; // need to add a single dropdown item height otherwise there will be a bit of white space at the bottom due to the filtered caption, probably due to the way position sticky works
      }

      return {
        transform: `translateY(${offset}px)`,
      };
    },

    filteredItemsCount: function() {
      return (this.debouncedDropdownText || "").trim() !== "" ? this.matchingItems.length : this.totalItems;
    },

    showNoMatchFoundIndicator: function() {
      return !this.isLoading && this.matchingItems.length === 0 && !this.allowFreeText && (this.debouncedDropdownText || "").trim();
    },
    spaceOccupiedByNoMatchFoundIndicator: function() {
      return this.showNoMatchFoundIndicator ? dropdownItemHeight : 0;
    },
    noMatchFoundIndicatorIsVisible: function() {
      return this.showNoMatchFoundIndicator && this.dropdownItemsWindowPosition.start === 0;
    },
    noMatchFoundIndicatorText: function() {
      return `No match '${this.debouncedDropdownText}'`;
    },

    showUseFreeTextIndicator: function() {
      return !this.isLoading && this.allowFreeText && (this.debouncedDropdownText || "").trim() && !this.matchingItems.find((item) => {
        return String(item?.[this.itemKey] || "").toLowerCase() === (this.debouncedDropdownText || "").trim().toLowerCase(); // if an exact match exists, don't display the indicator
      });
    },
    spaceOccupiedByUseFreeTextIndicator: function() {
      return this.showUseFreeTextIndicator ? dropdownItemHeight : 0;
    },
    useFreeTextIndicatorIsVisible: function() {
      return this.showUseFreeTextIndicator && this.dropdownItemsWindowPosition.start === 0;
    },
    useFreeTextIndicatorText: function() {
      return `Use '${ this.debouncedDropdownText }'`;
    },

    spaceOccupiedByMatchingItems: function() {
      return this.matchingItems.length * dropdownItemHeight;
    },
    visibleMatchingItems: function() { // virtual scrolling, the idea is to only render the items that is visible to the user, and remove everything else
      if (this.matchingItems.length === 0) {
        return [];
      }

      const distanceFromTopToMatchingItems = this.spaceOccupiedByNoMatchFoundIndicator + this.spaceOccupiedByUseFreeTextIndicator;
      const dropdownItemsWindowPosition = this.dropdownItemsWindowPosition;
      if (
        this.dropdownItemsWindowPosition.start > distanceFromTopToMatchingItems + this.spaceOccupiedByMatchingItems ||
        this.dropdownItemsWindowPosition.end < distanceFromTopToMatchingItems
      ) {
        return [];
      }

      let indexToStartRenderingFrom;
      let numberOfItemsToRender;
      if (dropdownItemsWindowPosition.start > distanceFromTopToMatchingItems) {
        indexToStartRenderingFrom = Math.ceil((dropdownItemsWindowPosition.start - distanceFromTopToMatchingItems) / dropdownItemHeight);
        numberOfItemsToRender = Math.ceil((dropdownItemsWindowPosition.end - dropdownItemsWindowPosition.start) / dropdownItemHeight);
      } else {
        indexToStartRenderingFrom = 0;
        numberOfItemsToRender = Math.ceil((dropdownItemsWindowPosition.end - distanceFromTopToMatchingItems) / dropdownItemHeight);
      }
      return this.matchingItems.slice(indexToStartRenderingFrom, indexToStartRenderingFrom + numberOfItemsToRender);
    },

    showSplitter: function() {
      if (this.isLoading) {
        return false;
      }

      return (
        this.showNoMatchFoundIndicator ||
        this.showUseFreeTextIndicator ||
        this.matchingItems.length > 0
      ) && (
        this.showNoItemsIndicator ||
        this.dropdownItems.length > 0
      );
    },
    spaceOccupiedBySplitter: function() {
      return this.showSplitter ? splitterHeight : 0;
    },
    splitterIsVisible: function() {
      return (
        this.noMatchFoundIndicatorIsVisible ||
        this.useFreeTextIndicatorIsVisible ||
        this.visibleMatchingItems.length > 0
      ) && (
        this.noItemsIndicatorIsVisible ||
        this.visibleDropdownItems.length > 0
      );
    },

    showNoItemsIndicator: function() {
      return !this.isLoading && this.totalItems === 0;
    },
    noItemsIndicatorIsVisible: function() {
      if (!this.showNoItemsIndicator) {
        return false;
      }

      const distanceFromTopToNoItemsIndicator = this.spaceOccupiedByNoMatchFoundIndicator + this.spaceOccupiedByUseFreeTextIndicator + this.spaceOccupiedByMatchingItems + this.spaceOccupiedBySplitter;
      return this.dropdownItemsWindowPosition.end >= distanceFromTopToNoItemsIndicator; // no items indicator will always be at the bottom of the list
      // only need to check if the end of the window is within range, as there will not be further segments after the indicator
    },

    visibleDropdownItems: function() {
      if (this.dropdownItems.length === 0) {
        return [];
      }

      const distanceFromTopToDropdownItems = this.spaceOccupiedByNoMatchFoundIndicator + this.spaceOccupiedByUseFreeTextIndicator + this.spaceOccupiedByMatchingItems + this.spaceOccupiedBySplitter;
      const dropdownItemsWindowPosition = this.dropdownItemsWindowPosition;
      if (this.dropdownItemsWindowPosition.end < distanceFromTopToDropdownItems) { // dropdown items segment will always be at the bottom of the list
        return []; // only need to check if the end of the window is within range, as there will not be further segments after dropdown items
      }

      let indexToStartRenderingFrom;
      let numberOfItemsToRender;
      if (dropdownItemsWindowPosition.start > distanceFromTopToDropdownItems) {
        indexToStartRenderingFrom = Math.ceil((dropdownItemsWindowPosition.start - distanceFromTopToDropdownItems) / dropdownItemHeight);
        numberOfItemsToRender = Math.ceil((dropdownItemsWindowPosition.end - dropdownItemsWindowPosition.start) / dropdownItemHeight);
      } else {
        indexToStartRenderingFrom = 0;
        numberOfItemsToRender = Math.ceil((dropdownItemsWindowPosition.end - distanceFromTopToDropdownItems) / dropdownItemHeight);
      }

      return this.dropdownItems.slice(indexToStartRenderingFrom, indexToStartRenderingFrom + numberOfItemsToRender);
    },
  },
  data: function() {
    return {
      inputFocused: false,

      dropdownIndexSymbol: Symbol("dropdownIndex"), // this data member should never change throughout the lifecycle of this component, it should be read only
      // used as a unique key to add index numbers to each dropdown item https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Symbol
      // this key and the associated value should only be present within the component, it should not be emitted back to the parent page

      dropdownItemsAndMatchingItemsInitialised: false, // starts of as false when the component is first rendered
      // changed to true once drodownItems and matchingItems has been populated for the first time

      dropdownItems: [],
      matchingItems: [],
      privateSelectedObject: null,

      activeDropdownItemIndex: -1, // 0 is the use free text indicator, the actual dropdown items start from 1

      dropdownText: '',
      debouncedDropdownText: '',
      debounceTimeoutID: null,

      dropdownItemsWindowVisibleHeight: 275,
      dropdownListScrollTop: 0,

      privateErrorMessage: '',

      teleportedList: null,
      
    };
  },
  props: {
    items: { type: Array, default: function() { return []; } }, // The items in the dropdown
    itemKey: { type: String, default: '' }, // The key of the item in the dropdown, if leave it as empty string, it will automatically takes the second key in the object
    placeholder: { type: String, default: 'Placeholder' }, // Set the placeholder of the dropdown
    selectedObject: { type: Object, default: function() { return null; } }, // The selected object in the dropdown
    isDisabled: { type: Boolean, default: false }, // Set as true, and the user will not be able to select or type in the dropdown
    isLoading: { type: Boolean, default: false }, // Set as true while dropdown items are being loaded
    isRequired: { type: Boolean, default: false }, // Set this to true to indicate this dropdown is required
    errorMessage: { type: String, default: '' }, // Set the error message of the dropdown
    allowFreeText: { type: Boolean, default: false }, // Set to false if user have to select from the dropdown
    search: { type: String, default: '' }, // Get the value of the input
    textAlign: { type: String, default: '' }, // Set this to left, center or right
    validator: { type: String, default: '' }, // validator for input value, refer to input component for the list of available validators
    validatorFunction: { type: Function, default: null }, // custom validator function for input value, refer to input component for the function signature
    keepOpenOnSelect: { type: Boolean, default: false }, // Set to true to select items by click/Enter without closing the list (multi-pick); the search text clears after each pick
    isItemChecked: { type: Function, default: null }, // Optional (item) => Boolean; when set, each row shows a checkbox reflecting the returned state
    pinCheckedItems: { type: Boolean, default: false }, // With isItemChecked: when the search is empty, checked rows use the top (matching) section and the rest sit below the splitter

    // Public events
    // @selected(newSelectedObject: Object | null, oldSelectedObject: Object | null) - fired when an item is selected, returns the selected item
    // @focusChanged(focusedState: Boolean) - fired when the input focused is changed, returns true if the input is focused
    // @keydown(event: KeyboardEvent) - fired when a key is pressed while the input is focused on
  },
  methods: {
    updateDropdownItemsAndMatchingItems: function() {
      this.dropdownItemsAndMatchingItemsInitialised = true;
      const dropdownText = (this.debouncedDropdownText || "").trim();

      if (dropdownText === "" && this.pinCheckedItems && this.isItemChecked) {
        // reuse the search split: checked rows on top, splitter, then unchecked rows (source order kept in both)
        const checkedItems = [];
        const otherItems = [];
        for (const item of this.items) {
          if (item["#"] === "0" || item["#"] === 0) {
            continue;
          }
          (this.isItemChecked(item) ? checkedItems : otherItems).push(item);
        }
        this.matchingItems = checkedItems.map((item, index) => ({ ...item, [this.dropdownIndexSymbol]: index + 1 }));
        this.dropdownItems = otherItems.map((item, index) => ({ ...item, [this.dropdownIndexSymbol]: checkedItems.length + index + 1 }));
        return;
      }

      if (dropdownText === "") {
        this.dropdownItems = [];
        let currentIndex = 1;
        for (const item of this.items) {
          if (item["#"] === "0" || item["#"] === 0) { // the item with # equal to 0 is the data type, not the actual data, it's meant for tablebox not dropdown, ignore this item
            continue;
          }

          this.dropdownItems.push({
            ...item,
            [this.dropdownIndexSymbol]: currentIndex,
          });
          currentIndex++;
        }

        this.matchingItems = [];
        return;
      }

      this.dropdownItems = [];
      this.matchingItems = [];
      let currentIndex = 1;
      const formattedDebouncedDropdownText = dropdownText.toLowerCase();
      for (const item of this.items) {
        if (item["#"] === "0" || item["#"] === 0) { // the item with # equal to 0 is the data type, not the actual data, it's meant for tablebox not dropdown, ignore this item
          continue;
        }

        const itemValue = String(item?.[this.itemKey] || "").toLowerCase();

        if (itemValue.includes(formattedDebouncedDropdownText)) {
          this.matchingItems.push({
            ...item,
            [this.dropdownIndexSymbol]: currentIndex,
          });

          currentIndex++;
        } else {
          this.dropdownItems.push(item);
        }
      }

      this.dropdownItems = this.dropdownItems.map((item, index) => ({
        ...item,
        [this.dropdownIndexSymbol]: currentIndex + index,
      }));
    },

    inputFocusChanged: async function(focusedState) {
      this.inputFocused = focusedState;
      this.$emit('focusChanged', focusedState);

      if (focusedState) { // input was focused in on
        this.dropdownListScrollTop = 0;
        this.activeDropdownItemIndex = -1;

        if (!this.dropdownItemsAndMatchingItemsInitialised) {
          // only initialise when the dropdown is first expanded, otherwise the page may freeze for a while if there are a lot of dropdowns to render
          // updating can take around 0.6 to 0.8 seconds for around 100,000 items, during that time the page will be frozen
          this.updateDropdownItemsAndMatchingItems();

          // if the dropdown is closed and reopened, the same dropdownItems and matchingItems can be reused, there's no need to reupdate
        }

        this.$nextTick(() => {
          const dropdownList = this.$refs.dropdownList;
          if (dropdownList && !this.teleportedList) {
            this.$getOverlayRoot().appendChild(dropdownList);
            this.teleportedList = dropdownList;
            dropdownList.style.position = 'fixed';
            dropdownList.style.zIndex = '99998';
          }
          this.positionDropdownList();
        
          const filteredCaptionHeight = 25;
          this.dropdownItemsWindowVisibleHeight = dropdownList.clientHeight - filteredCaptionHeight;
        
          window.addEventListener('scroll', this.onScroll, true);
          window.addEventListener('resize', this.onScroll);
        });
        return;
      }

      this.detachDropdownList();

      if (this.isLoading) {
        return;
      }

      // input was focused out of
      // the logic for deciding which item to select is all here

      if (this.keepOpenOnSelect) { // picks happen explicitly on click/Enter; optional free text is added on blur
        const freeText = this.allowFreeText ? this.dropdownText.trim() : '';
        if (this.debounceTimeoutID !== null) {
          clearTimeout(this.debounceTimeoutID);
          this.debounceTimeoutID = null;
        }
        this.activeDropdownItemIndex = -1;
        this.updateDropdownTextAndSearch("");
        if (freeText) this.$emit('freeTextSelected', freeText);
        return;
      }

      if (this.debounceTimeoutID !== null) { // there is a pending debounce action to update debouncedDropdownText
        clearTimeout(this.debounceTimeoutID); // cancel the debounced action
        this.updateDebouncedDropdownText(this.dropdownText); // update debouncedDropdownText immediately
        await this.$nextTick(); // wait for the watcher to update dropdownItems and matchingItems
      }

      if (this.activeDropdownItemIndex !== -1) { // if an item was highlighted (either by being hovered over, or with the arrow keys)
        if (this.activeDropdownItemIndex === 0) { // select the highlighted item
          this.selectFreeText();
        } else if (this.activeDropdownItemIndex <= this.matchingItems.length) {
          this.selectMatchingItem(this.activeDropdownItemIndex);
        } else {
          this.selectDropdownItem(this.activeDropdownItemIndex);
        }

        return;
      }

      // no item was highlighted

      const trimmedDropdownText = this.dropdownText.trim();
      if (trimmedDropdownText === "") { // dropdown text was cleared off
        this.updateSelectedObjectDataAndProp(null);
        this.updateDropdownTextAndSearch("");
        return;
      }//logic moved up because it doesn't clear the parent searchInput if it's the other way around.

      if (trimmedDropdownText === (this.privateSelectedObject?.[this.itemKey] ?? "")) { // nothing changed
        this.updateDropdownTextAndSearch(trimmedDropdownText); // just in case the user entered a few whitespace characters and focused out
        return;
      }  

      if (this.matchingItems.length === 1) { // there's only one item that contains the dropdown text
        this.selectMatchingItem(1); // just select it, even though it isn't an exact match
        return;
      }

      const exactMatchFoundInMatchingItems = this.matchingItems.find((item) => { // can't use showUseFreeTextIndicator computed property to determine if an exact match exists
        return String(item?.[this.itemKey] || "").toLowerCase() === trimmedDropdownText.toLowerCase(); // because that uses the debounced dropdown text, whereas this function should use the real time dropdown text
      }); // and also because the computed property doesn't return the actual matching object, it just returns whether it exists or not
      if (exactMatchFoundInMatchingItems) {
        this.selectMatchingItem(exactMatchFoundInMatchingItems[this.dropdownIndexSymbol]);
        return;
      }

      const exactMatchFoundInDropdownItems = this.dropdownItems.find((item) => {
        return String(item?.[this.itemKey] || "").toLowerCase() === trimmedDropdownText.toLowerCase();
      });
      if (exactMatchFoundInDropdownItems) {
        this.selectDropdownItem(exactMatchFoundInDropdownItems[this.dropdownIndexSymbol]);
        return;
      }

      if (!this.allowFreeText) {
        await this.$showAlert('error', 'Record Not Found', `Could not find record for '${trimmedDropdownText}'.<br>Please select item that is within the list.`);
        this.setFocus();
      } else {
        this.selectFreeText();
      }
    },
    dropdownTextInputChanged: function(newValue, _oldValue) {
      if (this.debounceTimeoutID !== null) {
        clearTimeout(this.debounceTimeoutID);
      }

      this.debounceTimeoutID = setTimeout(() => {
        this.updateDebouncedDropdownText(newValue);
      }, 300);

      this.$nextTick(() => { // needs to be wrapped in next tick, because the watcher needs to run after dropdownText has changed
        this.$emit('update:search', newValue); // otherwise the watcher will break the debounce
      });
    },
    updateDebouncedDropdownText: function(newDropdownText) {
      this.debouncedDropdownText = newDropdownText; // the watcher will update dropdownItems and matchingItems, and in turn the computed properties will update the UI accordingly
      this.debounceTimeoutID = null;
      this.updateDropdownListScrollTop(0);
      this.activeDropdownItemIndex = -1;
    },
    inputKeyDown: function(keyboardEvent) {
      if (this.isLoading) {
        return;
      }

      switch (keyboardEvent.key) {
        case "ArrowDown": {
          if (this.activeDropdownItemIndex >= this.totalItems) { // index should never be more than the number of items, at most it should be equal
            break; // but handle those situations anyway, just in case
          }

          if (this.activeDropdownItemIndex === -1) {
            if (this.showUseFreeTextIndicator) {
              this.activeDropdownItemIndex = 0;
            } else {
              this.activeDropdownItemIndex = 1;
            }
          } else {
            this.activeDropdownItemIndex += 1;
          }

          const lastVisibleDropdownItemIndex = this.visibleDropdownItems.length !== 0 ? this.visibleDropdownItems.at(-1)[this.dropdownIndexSymbol] : this.visibleMatchingItems.at(-1)[this.dropdownIndexSymbol];
          if (this.activeDropdownItemIndex > lastVisibleDropdownItemIndex - Math.ceil(this.dropdownItemsWindowPosition.bottomPaddingHeight / dropdownItemHeight) - 2) {
            this.updateDropdownListScrollTop(this.dropdownListScrollTop + dropdownItemHeight + 1);
          }
          break;
        }

        case "ArrowUp": {
          if (this.activeDropdownItemIndex <= 0) { // index should never be less than 0, at most it should be equal
            break; // but handle those situations anyway, just in case
          }

          if (this.activeDropdownItemIndex === 1) {
            if (this.showUseFreeTextIndicator) {
              this.activeDropdownItemIndex = 0;
            } else {
              this.activeDropdownItemIndex = -1;
            }
          } else {
            this.activeDropdownItemIndex -= 1;
          }

          const firstVisibleDropdownItemIndex = this.visibleMatchingItems.length !== 0 ? this.visibleMatchingItems[0][this.dropdownIndexSymbol] : this.visibleDropdownItems[0][this.dropdownIndexSymbol];
          if (this.activeDropdownItemIndex < firstVisibleDropdownItemIndex + Math.ceil(this.dropdownItemsWindowPosition.topPaddingHeight / dropdownItemHeight) + 2) {
            this.updateDropdownListScrollTop(this.dropdownListScrollTop - dropdownItemHeight - 1);
          }
          break;
        }

        case "Enter":
          if (this.keepOpenOnSelect) {
            if (this.activeDropdownItemIndex > 0) {
              keyboardEvent.preventDefault();
              this.chooseItemKeepOpen(this.activeDropdownItemIndex);
            }
            break;
          }
          if (this.activeDropdownItemIndex !== -1) { // arrow down and arrow up handlers should prevent index from being 0 if free text isn't allowed
            this.setBlur(); // this will trigger the inputFocusChanged handler, which will select the item and update the necessary data and props
          }
          break;
      }

      this.$emit("keydown", keyboardEvent);
    },

    arrowClicked: function(pointerEvent) {
      pointerEvent.preventDefault();
      // if inputFocused is false, this event handler will setFocus, which will then trigger inputFocusChanged and populate the dropdown accordingly
      // immediately after, the input will be automatically blurred, because the user clicked on the arrow, which is outside the input component
      // which will then bring the focus out of the input, collapse the dropdown, and trigger inputFocusChanged again
      // thus, preventDefault is needed to prevent the input from automatically blurring

      if (this.isDisabled) {
        return;
      }

      if (this.inputFocused) { // if input is currently focused on
        this.$nextTick(() => {
          this.setBlur(); // blur out of the input, this will trigger inputFocusChanged which will handle the rest of the logic
        });
      } else { // else if input is not focused on
        this.$nextTick(() => {
          this.setFocus(); // focus on the input, this will trigger inputFocusChanged which will handle the rest of the logic
        });
      }
    },
    clearFilterClicked: function(mouseEvent) {
      mouseEvent.preventDefault();
      // clicking on the clear filter icon will bring focus out of the input, because the image is outside of the input
      // preventDefault is needed to prevent that

      this.updateSelectedObjectDataAndProp(null);
      this.updateDropdownTextAndSearch("");
    },

    dropdownListScrolled: function(e) {
      this.dropdownListScrollTop = e.target.scrollTop;
    },
    dropdownItemMouseDown: function(mouseEvent, dropdownIndex) {
      mouseEvent.preventDefault(); // keep focus in the input so the list stays open
      this.chooseItemKeepOpen(dropdownIndex);
    },
    chooseItemKeepOpen: function(dropdownIndex) { // keepOpenOnSelect: emit the item without closing the list or writing its label into the input
      const sourceItem = dropdownIndex <= this.matchingItems.length ? this.matchingItems[dropdownIndex - 1] : this.dropdownItems[dropdownIndex - this.matchingItems.length - 1];
      if (!sourceItem) {
        return;
      }
      const selectedItem = { ...sourceItem };
      delete selectedItem[this.dropdownIndexSymbol];
      this.$emit('selected', selectedItem, this.selectedObject);
      if (this.debounceTimeoutID !== null) {
        clearTimeout(this.debounceTimeoutID);
        this.debounceTimeoutID = null;
      }
      this.updateDropdownTextAndSearch("");
      this.activeDropdownItemIndex = -1;
    },
    dropdownItemMouseEnter: function(dropdownIndex) {
      this.activeDropdownItemIndex = dropdownIndex;
    },
    dropdownItemMouseLeave: function(dropdownIndex) {
      if (this.activeDropdownItemIndex === dropdownIndex) { // to prevent item from being unhighlighted if the highlighted item has been changed via the arrow keys
        this.activeDropdownItemIndex = -1; // without this, if the user hovered over an item, highlighting it, changed the highlighted item via the arrow keys, and moved the cursor out of the dropdown list, the item would be unhighlighted
      }
    },

    updateDropdownListScrollTop: function(newScrollTop) {
      const list = this.teleportedList || this.$refs.dropdownList;
      if (list) list.scrollTop = newScrollTop;      
    },
    selectFreeText: function() {
      this.updateSelectedObjectDataAndProp(null);
      this.updateDropdownTextAndSearch(this.dropdownText);
      this.$emit('freeTextSelected', this.dropdownText.trim());
    },
    selectMatchingItem: function(index) { // index passed in is the dropdown index assigned in matchingItems and dropdownItems
      const selectedItem = { // clone object, otherwise the symbol will be deleted from the original object in matchingItems as well
        ...this.matchingItems[index - 1], // need to convert them to the actual array indexes when updating selected object
      };
      delete selectedItem[this.dropdownIndexSymbol]; // remove symbol before emitting back to parent component

      this.updateSelectedObjectDataAndProp(selectedItem);
      this.updateDropdownTextAndSearch(selectedItem[this.itemKey]);
    },
    selectDropdownItem: function(index) { // index passed in is the dropdown index assigned in matchingItems and dropdownItems
      const selectedItem = { // clone object, otherwise the symbol will be deleted from the original object in dropdownItems as well
        ...this.dropdownItems[index - this.matchingItems.length - 1], // need to convert them to the actual array indexes when updating selected object
      };
      delete selectedItem[this.dropdownIndexSymbol]; // remove symbol before emitting back to parent component

      this.updateSelectedObjectDataAndProp(selectedItem);
      this.updateDropdownTextAndSearch(selectedItem[this.itemKey]);
    },
    updateSelectedObjectDataAndProp: function(selectedItem) {
      this.privateSelectedObject = selectedItem;
      this.$emit('update:selectedObject', selectedItem);
      this.$emit('selected', selectedItem, this.selectedObject);
    },
    updateDropdownTextAndSearch: function(searchValue) {
      this.dropdownText = searchValue;
      this.debouncedDropdownText = searchValue;
      this.$emit('update:search', searchValue);
    },
    // eslint-disable-next-line vue/no-unused-properties -- public method used by MultiSelect (checklist), just not used within this component
    refreshItemGrouping: function() { // re-split rows after the parent's checked state changes (pinCheckedItems)
      if (this.dropdownItemsAndMatchingItemsInitialised) {
        this.updateDropdownItemsAndMatchingItems();
      }
    },
    setFocus: function() { // public method, but also used within this component
      this.$refs.input.setFocus();
    },
    setBlur: function() { // public method, but also used within this component
      this.$refs.input.setBlur();
    },
    // =================== maybe add the scroll into view method?

    getTriggerRect() {
      return this.$el.getBoundingClientRect();
    },

    positionDropdownList() {
      if (!this.teleportedList) return;
        
      const rect = this.getTriggerRect();
      const dropdownList = this.teleportedList;
      const viewportPadding = 5;
        
      // Set width first because CSS min-width may make the actual width
      // larger than the input width.
      dropdownList.style.width = rect.width + 'px';
        
      const listHeight = dropdownList.offsetHeight;
      const listWidth = dropdownList.offsetWidth;
        
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
        
      const goUpwards =
        spaceBelow < listHeight &&
        spaceAbove > spaceBelow;
        
      if (goUpwards) {
        dropdownList.style.top =
          Math.max(viewportPadding, rect.top - listHeight) + 'px';
      
        dropdownList.classList.add('upwards');
      } else {
        dropdownList.style.top =
          Math.min(
            rect.bottom,
            window.innerHeight - listHeight - viewportPadding
          ) + 'px';
      
        dropdownList.classList.remove('upwards');
      }
    
      // Normally align with the input's left edge.
      let left = rect.left;
    
      // If it overflows the right edge, align its right edge
      // with the input's right edge.
      if (left + listWidth > window.innerWidth - viewportPadding) {
        left = rect.right - listWidth;
      }
    
      // If it still overflows the left edge, clamp it inside the viewport.
      left = Math.max(viewportPadding, left);
    
      // Final right-edge clamp for dropdowns wider than the available viewport.
      left = Math.min(
        left,
        window.innerWidth - listWidth - viewportPadding
      );
    
      dropdownList.style.left =
        Math.max(viewportPadding, left) + 'px';
    },

    onScroll() {
      if (this.inputFocused) {
        requestAnimationFrame(() => {
          this.positionDropdownList();
        });
      }
    },

    detachDropdownList() {
      window.removeEventListener('scroll', this.onScroll, true);
      window.removeEventListener('resize', this.onScroll);
      if (this.teleportedList && this.teleportedList.parentNode) {
        this.teleportedList.parentNode.removeChild(this.teleportedList);
      }
      this.teleportedList = null;
    },

    
  },
  mounted() {
    if (this.selectedObject) {
      this.privateSelectedObject = this.selectedObject;
      this.updateDropdownTextAndSearch(this.selectedObject[this.itemKey]);
    } else {
      this.updateDropdownTextAndSearch(this.search);
    }

    if (this.errorMessage) {
      this.privateErrorMessage = this.errorMessage;
    }
  },
  watch: {
    items: function() {
      if (this.dropdownItemsAndMatchingItemsInitialised) {
        // only update if the dropdown was expanded at least once
        this.updateDropdownItemsAndMatchingItems();
      }
    },
    debouncedDropdownText: function() {
      this.updateDropdownItemsAndMatchingItems();
    },
    selectedObject: function(val) {
      this.privateSelectedObject = val;
      if (!this.allowFreeText || val) { // if free text is disallowed, always update the dropdown text according to the selected object
        this.updateDropdownTextAndSearch(val?.[this.itemKey] ?? ""); // if free text is allowed, update dropdown text if selected object is not null
      } // when the user chooses to use free text, the selected object would be null but the dropdown text and search values shouldn't be blank, instead let the parent page control that with the search prop
    },
    errorMessage: function(val) {
      this.privateErrorMessage = val;
    },
    privateErrorMessage: function(val) {
      this.$emit('update:errorMessage', val);
    },
    search: function(val) {
      if (val !== this.dropdownText) { // to prevent updating of dropdownText and debouncedDropdownText when the user typed into the dropdown
        this.updateDropdownTextAndSearch(val); // only update it when the parent page changes the prop, otherwise it would break the debounce behaviour
      }
    },
  },

  beforeDestroy() {
    this.detachDropdownList();
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.dropdown-main {
  width: 100%;
  position: relative;
  height: 25px;
  margin: 2px 0;
  padding: 0;

  > .dropdown-input {
    width: calc(100% - 10px);
    margin: 0;
  }

  > .arrow-container {
    position: absolute;
    top: 0px;
    right: 0px;
    height: 20px;
    width: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    > .arrow-down {
      border-width: 6px 6px 0px 6px;
      border-style: solid;
      border-color: rgba(64,64,64,1) transparent transparent transparent;
      border-radius: 10px;
      // z-index: 110;
      transition: 0.3s;
    }

    > .arrow-disabled {
      opacity: 0.3;
    }

    > .arrow-flip {
      transform: scaleY(-1);
    }
  }

  > .dropdown-list {
    width: 100%;
    min-width: 200px;
    height: fit-content;
    max-height: 300px;
    padding: 5px 0;
    padding-top: 0;
    background-color: white;
    border-color: rgb(150,150,150);
    border-style: solid;
    border-width: 0 1px 1px 1px;
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    overflow: auto;
    font-size: 12px;

    box-shadow: 0px 2px 8px -4px rgba(0, 0, 0, 0.25);

    > .filtered-caption {
      font-size: 10px;
      display: flex;
      flex-wrap: wrap; // narrow lists wrap caption parts onto a second line instead of squeezing them
      row-gap: 2px;
      align-items: center;
      justify-content: flex-end;
      padding: 5px 10px;
      top: 0;
      position: sticky;
      background-color: white;
      width: 100%;
      z-index: 1;

      > .filtered-count {
        display: inline-flex;
        align-items: center;
        margin-left: auto;
        white-space: nowrap;
      }

      > .filtered-count > img {
        margin-left: 5px;
        cursor: pointer;

        &:hover {
          background-color: rgba(200,200,200,0.5);
        }
      }
    }

    &.upwards {
      border-width: 1px 1px 0 1px;
      border-radius: 8px 8px 0 0;
    }
  }
}

.dropdown-item-check {
  margin: 0 5px 0 0;
  vertical-align: -2px;
  pointer-events: none;
}

.dropdown-items {
  margin: 1px;
  padding: 2px 5px 2px 5px;
  background-color: rgba(200,200,200,0.5);
  color: black;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
}

.item-error {
  background-color: rgba(255,0,0,0.2);
  cursor: default;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.item-loading {
  cursor: default;
}

.item-active {
  color: white;
  background-color: rgba(0,0,0,0.5);
}

.dropdown-splitter {
  height: 2px;
  margin: 5px 0;
  background-color: rgb(150,150,150);
  width: 100%;
}
</style>

<style lang="scss">
.dropdown-list {
  width: 100%;
  min-width: 200px;
  height: fit-content;
  max-height: 300px;
  padding: 5px 0;
  padding-top: 0;
  background-color: white;
  border-color: rgb(150,150,150);
  border-style: solid;
  border-width: 0 1px 1px 1px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
  overflow: auto;
  font-size: 12px;

  box-shadow: 0px 2px 8px -4px rgba(0, 0, 0, 0.25);

  > .filtered-caption {
    font-size: 10px;
    display: flex;
    flex-wrap: wrap; // narrow lists wrap caption parts onto a second line instead of squeezing them
    row-gap: 2px;
    align-items: center;
    justify-content: flex-end;
    padding: 5px 10px;
    top: 0;
    position: sticky;
    background-color: white;
    width: 100%;
    z-index: 1;

    > .filtered-count {
      display: inline-flex;
      align-items: center;
      margin-left: auto;
      white-space: nowrap;
    }

    > .filtered-count > img {
      margin-left: 5px;
      cursor: pointer;

      &:hover {
        background-color: rgba(200,200,200,0.5);
      }
    }
  }

  &.upwards {
    border-width: 1px 1px 0 1px;
    border-radius: 8px 8px 0 0;
  }
}

.dropdown-item-check {
  margin: 0 5px 0 0;
  vertical-align: -2px;
  pointer-events: none;
}

.dropdown-items {
  margin: 1px;
  padding: 2px 5px 2px 5px;
  background-color: rgba(200,200,200,0.5);
  color: black;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
}

.item-error {
  background-color: rgba(255,0,0,0.2);
  cursor: default;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.item-active {
  color: white;
  background-color: rgba(0,0,0,0.5);
}

.dropdown-splitter {
  height: 2px;
  margin: 5px 0;
  background-color: rgb(150,150,150);
  width: 100%;
}
</style>
